import {
  Time,
  ITSEventType,
  RawEvalPageData,
  EvalAnswers,
  EvalOption,
  TagResult,
  Tag,
  TagsAvoid,
} from '../common/page-data';
import { DiscourseData } from './discourse-data';
import { PageDataParams } from './page-data';

// threshold to detect a pause in milliseconds
const PAUSE_THRESHOLD = 30000;

export class EvalData extends DiscourseData {
  public constructor(params: PageDataParams) {
    super(params);
    const { rawPageData } = params;
    const {
      evalAnswers = {},
      firstResponse = '',
      secondResponse = '',
      tagsToAvoid,
      customQuestionPrompt
    } = rawPageData as RawEvalPageData;
    this._evalAnswers = evalAnswers;
    this._firstResponse = firstResponse;
    this._secondResponse = secondResponse;
    this._tagsToAvoid = tagsToAvoid;
    this._customQuestionPrompt = customQuestionPrompt;
  }

  private _firstResponse: string;
  private _secondResponse: string;
  private _evaluationStartTime = 0;
  private _evaluationEndTime = 0;
  private _evaluationPauseTimer = 0;
  private _reevaluationStartTime = 0;
  private _reevaluationEndTime = 0;
  private _reevaluationPauseTimer = 0;
  private _evalAnswers: EvalAnswers;
  private _tagsToAvoid: TagsAvoid;
  private _customQuestionPrompt: string | undefined;

  /**
   * Getter tagsToAvoid
   * @return {TagsAvoid}
   */
  public get tagsToAvoid(): TagsAvoid {
    return this._tagsToAvoid;
  }

  public get evalAnswers(): EvalAnswers {
    return this._evalAnswers;
  }

  public set evalAnswers(value: EvalAnswers) {
    this._evalAnswers = value;
  }
  /**
   * Getter evaluationStartTime
   * @return {number}
   */
  public get evaluationStartTime(): number {
    return this._evaluationStartTime;
  }

  /**
   * Setter evaluationStartTime
   * @param {number} value
   */
  public set evaluationStartTime(value: number) {
    this._evaluationStartTime = value;
  }

  /**
   * Getter evaluationEndTime
   * @return {number}
   */
  public get evaluationEndTime(): number {
    return this._evaluationEndTime;
  }

  /**
   * Setter evaluationEndTime
   * @param {number} value
   */
  public set evaluationEndTime(value: number) {
    this._evaluationEndTime = value;
  }

  /**
   * Getter evaluationPauseTimer
   * @return {number}
   */
  public get evaluationPauseTimer(): number {
    return this._evaluationPauseTimer;
  }

  /**
   * Setter evaluationPauseTimer
   * @param {number} value
   */
  public set evaluationPauseTimer(value: number) {
    this._evaluationPauseTimer = value;
  }

  /**
   * Getter reevaluationStartTime
   * @return {number}
   */
  public get reevaluationStartTime(): number {
    return this._reevaluationStartTime;
  }

  /**
   * Setter reevaluationStartTime
   * @param {number} value
   */
  public set reevaluationStartTime(value: number) {
    this._reevaluationStartTime = value;
  }

  /**
   * Getter reevaluationEndTime
   * @return {number}
   */
  public get reevaluationEndTime(): number {
    return this._reevaluationEndTime;
  }

  /**
   * Setter reevaluationEndTime
   * @param {number} value
   */
  public set reevaluationEndTime(value: number) {
    this._reevaluationEndTime = value;
  }

  /**
   * Getter reevaluationPauseTimer
   * @return {number}
   */
  public get reevaluationPauseTimer(): number {
    return this._reevaluationPauseTimer;
  }

  /**
   * Setter reevaluationPauseTimer
   * @param {number} value
   */
  public set reevaluationPauseTimer(value: number) {
    this._reevaluationPauseTimer = value;
  }

  /**
   * Getter firstResponse
   * @return {string}
   */
  public get firstResponse(): string {
    return this._firstResponse;
  }
  /**
   * Setter firstResponse
   * @param {string} value
   */
  public set firstResponse(value: string) {
    const currentTime = Date.now();
    const timelapse = currentTime - this.evaluationPauseTimer;

    if (timelapse > PAUSE_THRESHOLD) {
      const time: Time = { time: timelapse };
      this.recordEvent(ITSEventType.evaluationPause, time);
    }

    this.evaluationPauseTimer = currentTime;

    this._firstResponse = value;
    this._secondResponse = value;
  }
  
  public get customQuestionPrompt(): string | undefined {
    return this._customQuestionPrompt;
  }


  /**
   * Getter secondResponse
   * @return {string}
   */
  public get secondResponse(): string {
    const currentTime = Date.now();
    const timelapse = currentTime - this.evaluationPauseTimer;

    if (timelapse > PAUSE_THRESHOLD) {
      const time: Time = { time: timelapse };
      this.recordEvent(ITSEventType.reevaluationPause, time);
    }

    this.evaluationPauseTimer = currentTime;
    return this._secondResponse;
  }

  /**
   * Setter secondResponse
   * @param {string} value
   */
  public set secondResponse(value: string) {
    this._secondResponse = value;
  }

  public startEvaluation() {
    this.evaluationStartTime = Date.now();
    this.evaluationPauseTimer = this.evaluationStartTime;
    this.recordEvent(ITSEventType.evaluationStartTime);
  }

  public endEvaluation() {
    const now = Date.now();
    const data: Time = {
      time: now - this.evaluationStartTime,
    };
    this.recordEvent(ITSEventType.evaluationEndTime, data);
    this.evaluationEndTime = now;
  }

  public startReevaluation() {
    this.reevaluationStartTime = Date.now();
    this.evaluationPauseTimer = this.reevaluationStartTime;
    this.recordEvent(ITSEventType.reevaluationStartTime);
  }

  public endReevaluation() {
    const now = Date.now();
    const data: Time = {
      time: now - this.reevaluationStartTime,
    };
    this.recordEvent(ITSEventType.reevaluationEndTime, data);
    this.evaluationEndTime = now;
  }

  public get tagResults(): TagResult[] {
    const { evalAnswers } = this;
    const tagResults: TagResult[] = [];

    Object.keys(evalAnswers).forEach((key) => {
      const correct = evalAnswers[key] === EvalOption.Correct;
      const tag: Tag = this.tags[key] || this.tagsToAvoid[key];

      const result: TagResult = { tagId: key, name: tag.name, correct };

      tagResults.push(result);
    });
    return tagResults;
  }

  public get score(): number {
    const values = Object.values(this.evalAnswers);
    const portion = values.length ? 1 / values.length : 0;
    const score = values.reduce((p: number, c: string): number => {
      switch (c) {
        case EvalOption.Correct:
          p += portion;
          break;
        case EvalOption.PartiallyCorrect:
          p += portion / 2;
          break;
        default:
      }
      return p;
    }, 0);

    return score;
  }

  public get rawData(): RawEvalPageData {
    const playStats = this.playStats;
    const firstResponse = this.firstResponse;
    const secondResponse = this.secondResponse;
    const evalAnswers = this.evalAnswers;

    const raw: RawEvalPageData = {
      ...(this._rawPageData as RawEvalPageData),
      playStats,
      firstResponse,
      secondResponse,
      evalAnswers: evalAnswers,
    };

    return raw;
  }
}
